import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  message,
} from 'antd';
import type { TableProps } from 'antd';
import { PlusOutlined, KeyOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  supabase,
  usePortalPermissions,
  provisionAuthUser,
  resetPasswordRedirectUrl,
  invokeEdgeFunction,
  formatDateTime,
  SetupCredentialsNotice,
  sendPasswordSetupLink,
  logAssistResetAudit,
  useAuthUser,
} from '@ubus/shared';
import AdminListPage from '../components/AdminListPage';
import StatusTag from '../components/StatusTag';
import { useAdminTableStability } from '../hooks/useAdminTableStability';
import { localeConfirm, localeSuccess } from '../utils/localeModal';

interface FleetTeamMember {
  auth_uid: string;
  email: string;
  display_name: string | null;
  status: string;
  role_id: string | null;
  role_code: string | null;
  role_name: string | null;
  is_primary: boolean;
  last_sign_in_at: string | null;
  created_at: string | null;
}

type FleetRoleCode = 'fleet_manager' | 'fleet_dispatcher' | 'fleet_read_only';

function localizedRoleName(
  code: string | null | undefined,
  fallback: string | null | undefined,
  t: TFunction
): string {
  if (code) {
    return t(`rbac.systemRoles.${code}.name`, { defaultValue: fallback ?? code });
  }
  return fallback ?? '—';
}

function TeamMembersPage() {
  const { t } = useTranslation();
  const { access, hasPermission, refresh: refreshAccess } = usePortalPermissions();
  const authUser = useAuthUser();
  const fleetOperatorId = access?.fleet_operator_id ?? null;
  const isPrimaryManager = Boolean(access?.is_primary_manager);

  const canManage = hasPermission('team.manage');
  const canProvision = hasPermission('team.manage');

  const [members, setMembers] = useState<FleetTeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [togglingUid, setTogglingUid] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm<{ email: string; display_name?: string; role_code: FleetRoleCode }>();
  const { pagination, onTableChange, rowClassName, afterMutation } = useAdminTableStability();

  const roleOptions = useMemo<{ value: FleetRoleCode; label: string }[]>(() => {
    const base: FleetRoleCode[] = ['fleet_dispatcher', 'fleet_read_only'];
    if (isPrimaryManager) base.unshift('fleet_manager');
    return base.map((code) => ({
      value: code,
      label: t(`rbac.systemRoles.${code}.name`, { defaultValue: code }),
    }));
  }, [isPrimaryManager, t]);

  const load = useCallback(async () => {
    if (!fleetOperatorId) {
      setMembers([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('list_fleet_team_members');
      if (error) {
        message.error(error.message);
        setMembers([]);
      } else {
        setMembers((data ?? []) as FleetTeamMember[]);
      }
    } finally {
      setLoading(false);
    }
  }, [fleetOperatorId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleRoleChange = async (row: FleetTeamMember, roleCode: FleetRoleCode) => {
    try {
      const { error } = await supabase.rpc('assign_fleet_team_role', {
        p_auth_uid: row.auth_uid,
        p_role_code: roleCode,
      });
      if (error) throw error;
      message.success(t('rbac.roleAssigned'));
      await afterMutation(row.auth_uid, load);
      await refreshAccess();
    } catch (e) {
      message.error(e instanceof Error ? e.message : t('rbac.saveFailed'));
      await load();
    }
  };

  const handleStatusToggle = async (row: FleetTeamMember, active: boolean) => {
    const nextStatus = active ? 'active' : 'disabled';
    setTogglingUid(row.auth_uid);
    try {
      const { error } = await supabase.rpc('set_fleet_member_status', {
        p_auth_uid: row.auth_uid,
        p_status: nextStatus,
      });
      if (error) throw error;

      const { error: efError } = await invokeEdgeFunction('provision-user', {
        body: {
          action: 'set_status',
          user_id: row.auth_uid,
          status: nextStatus,
        },
      });
      if (efError) {
        // Split-brain guard: profile was just disabled but auth ban failed.
        // Roll the profile back to active so we don't leave the user in a
        // state where the profile is disabled but the identity can still
        // sign in. Only roll back when we disabled (not when we enabled).
        if (nextStatus === 'disabled') {
          const { error: rollbackError } = await supabase.rpc(
            'set_fleet_member_status',
            { p_auth_uid: row.auth_uid, p_status: 'active' }
          );
          if (rollbackError) {
            message.warning(
              `${efError.message} — ${t('fleetOperators.members.rollbackFailed')}`
            );
          } else {
            message.warning(
              t('fleetOperators.members.disabledRolledBack', {
                defaultValue: 'Auth ban failed; profile rolled back to active.',
              })
            );
          }
        } else {
          message.warning(efError.message);
        }
      }

      message.success(t('fleetOperators.members.statusUpdated'));
      await afterMutation(row.auth_uid, load);
    } catch (e) {
      message.error(e instanceof Error ? e.message : t('fleetOperators.members.updateFailed'));
      await load();
    } finally {
      setTogglingUid(null);
    }
  };

  const handleSendSetupLink = (row: FleetTeamMember) => {
    if (!isPrimaryManager) {
      message.warning(t('team.resetPasswordPrimaryOnly'));
      return;
    }
    if (authUser?.id && row.auth_uid === authUser.id) {
      message.warning(t('team.resetPasswordNotSelf'));
      return;
    }
    const label = row.display_name || row.email;
    localeConfirm(t, {
      title: t('rbac.sendSetupLink'),
      content: t('rbac.setupLinkConfirm', { name: label }),
      okText: t('rbac.sendSetupLink'),
      onOk: async () => {
        try {
          const result = await sendPasswordSetupLink({
            email: row.email,
            userId: row.auth_uid,
            redirectTo: resetPasswordRedirectUrl(),
            sendEmail: true,
          });
          logAssistResetAudit({
            actorId: authUser?.id,
            targetType: 'fleet_user',
            targetId: row.auth_uid,
            channel: result.emailed ? 'email_link' : 'link_only',
            emailed: result.emailed,
          });
          localeSuccess(t, {
            title: t('rbac.setupLinkSent'),
            width: 560,
            content: (
              <SetupCredentialsNotice
                email={result.email || row.email}
                setupLink={result.setupLink}
                extraNote={
                  result.emailed
                    ? t('rbac.setupLinkEmailed', {
                        email: result.recipient || row.email,
                      })
                    : t('rbac.setupLinkShareHint')
                }
              />
            ),
          });
        } catch (err) {
          message.error(
            err instanceof Error ? err.message : t('rbac.setupLinkFailed')
          );
        }
      },
    });
  };

  const handleCreate = async () => {
    const values = await form.validateFields();
    if (!fleetOperatorId) {
      message.error(t('team.noFleetContext'));
      return;
    }
    setCreating(true);
    try {
      const auth = await provisionAuthUser({
        role: 'fleet_owner',
        email: values.email.trim(),
        fleetOperatorId,
        portalRoleCode: values.role_code,
        displayName: values.display_name?.trim() || undefined,
        redirectTo: resetPasswordRedirectUrl(),
      });
      setCreateOpen(false);
      form.resetFields();
      localeSuccess(t, {
        title: t('team.memberReadyTitle'),
        width: 560,
        content: <SetupCredentialsNotice email={auth.email} setupLink={auth.setupLink} />,
      });
      await afterMutation(auth.userId, load);
    } catch (e) {
      message.error(e instanceof Error ? e.message : t('rbac.saveFailed'));
    } finally {
      setCreating(false);
    }
  };

  const columns: TableProps<FleetTeamMember>['columns'] = [
    {
      title: t('fleetOperators.members.columns.name'),
      key: 'name',
      width: 220,
      ellipsis: true,
      render: (_, r) => (
        <Space size={6} wrap={false}>
          <span>{r.display_name || r.email.split('@')[0]}</span>
          {r.is_primary ? (
            <Tag color="gold">{t('fleetOperators.members.primaryTag')}</Tag>
          ) : null}
        </Space>
      ),
    },
    {
      title: t('rbac.email'),
      dataIndex: 'email',
      key: 'email',
      width: 220,
      ellipsis: true,
    },
    {
      title: t('fleetOperators.members.columns.role'),
      key: 'role',
      width: 200,
      render: (_, r) =>
        canManage && !r.is_primary ? (
          <Select
            style={{ width: 180 }}
            value={(r.role_code as FleetRoleCode) ?? undefined}
            options={roleOptions}
            onChange={(code) => void handleRoleChange(r, code as FleetRoleCode)}
          />
        ) : (
          localizedRoleName(r.role_code, r.role_name, t)
        ),
    },
    {
      title: t('fleetOperators.columns.status'),
      key: 'status',
      width: 120,
      render: (_, r) =>
        canManage ? (
          <Tooltip
            title={
              r.is_primary
                ? t('fleetOperators.members.cannotDisablePrimary')
                : undefined
            }
          >
            <Switch
              checked={r.status === 'active'}
              loading={togglingUid === r.auth_uid}
              disabled={r.is_primary}
              onChange={(checked) => void handleStatusToggle(r, checked)}
              checkedChildren={t('fleetOperators.members.active')}
              unCheckedChildren={t('fleetOperators.members.disabled')}
            />
          </Tooltip>
        ) : (
          <StatusTag status={r.status} />
        ),
    },
    {
      title: t('fleetOperators.members.columns.lastSignIn'),
      dataIndex: 'last_sign_in_at',
      key: 'last_sign_in_at',
      width: 180,
      render: (v: string | null) => (v ? formatDateTime(v) : '—'),
    },
    {
      title: t('fleetOperators.columns.actions'),
      key: 'actions',
      width: 160,
      fixed: 'right',
      render: (_, r) => (
        <Space size={8} wrap>
          {isPrimaryManager && authUser?.id !== r.auth_uid ? (
            <Button
              type="link"
              size="small"
              icon={<KeyOutlined />}
              onClick={() => handleSendSetupLink(r)}
            >
              {t('rbac.sendSetupLink')}
            </Button>
          ) : null}
        </Space>
      ),
    },
  ];

  if (!fleetOperatorId) {
    return (
      <AdminListPage title={t('team.title')} subtitle={t('team.subtitle')}>
        <Alert
          type="warning"
          showIcon
          message={t('team.noFleetContext')}
        />
      </AdminListPage>
    );
  }

  return (
    <>
      <AdminListPage
        title={t('team.title')}
        subtitle={t('team.subtitle')}
        resultCount={members.length}
        extra={
          canProvision ? (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                form.resetFields();
                form.setFieldsValue({ role_code: 'fleet_dispatcher' });
                setCreateOpen(true);
              }}
            >
              {t('team.addMember')}
            </Button>
          ) : null
        }
      >
        <Table
          rowKey="auth_uid"
          loading={loading}
          columns={columns}
          dataSource={members}
          pagination={pagination}
          onChange={onTableChange}
          rowClassName={rowClassName}
          scroll={{ x: 1000 }}
          locale={{ emptyText: t('fleetOperators.members.empty') }}
        />
      </AdminListPage>

      <Modal
        title={t('team.addMember')}
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={() => void handleCreate()}
        confirmLoading={creating}
        okText={t('rbac.create')}
        cancelText={t('common.cancel')}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="email"
            label={t('rbac.email')}
            rules={[{ required: true }, { type: 'email' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="display_name" label={t('rbac.displayName')}>
            <Input />
          </Form.Item>
          <Form.Item
            name="role_code"
            label={t('rbac.permissionRole')}
            rules={[{ required: true }]}
          >
            <Select options={roleOptions} />
          </Form.Item>
          <p style={{ color: 'rgba(0,0,0,0.45)', marginBottom: 0 }}>
            {t('team.identityFixedFleetOwner')}
          </p>
          {!isPrimaryManager ? (
            <p style={{ color: 'rgba(0,0,0,0.45)', marginBottom: 0 }}>
              {t('team.managerInviteOnlyPrimary')}
            </p>
          ) : null}
        </Form>
      </Modal>
    </>
  );
}

export default TeamMembersPage;
